import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";

import { getApplication } from "../../api/application";
import { fetchDictionary } from "../../api/genericData";
import { BackButton } from "../../components/buttons/backButton/BackButton";
import { Button } from "../../components/buttons/Button";
import { SearchLoader } from "../../components/loaders/searchLoader/SearchLoader";
import { DlpQuotationOverview } from "../../components/quotationOverview/dlp/DlpQuotationOverview";
import { DupQuotationOverview } from "../../components/quotationOverview/dup/DupQuotationOverview";
import { useDidMount } from "../../hooks/useDidMount";
import { useQueryParams } from "../../hooks/useQueryParams";
import styles from "./ApplicationDetails.module.scss";
import { applicationDetailsReducer, initialState } from "./ApplicationDetails.state";

interface Props {
  fields: Record<string, Field<string>>;
}

export const APPLICATION_ID_QUERY_PARAM = "applicationId";

export const ApplicationDetails = ({ fields }: Props) => {
  const queryParams = useQueryParams();
  const applicationId = queryParams.get(APPLICATION_ID_QUERY_PARAM);

  const [data, dispatch] = useReducer(applicationDetailsReducer, initialState);

  useDidMount(async () => {
    await retrieveApplication();
  });

  const retrieveApplication = async () => {
    if (!applicationId) {
      return;
    }

    try {
      dispatch({ type: "FETCH_DATA" });
      const { phrases } = await fetchDictionary();
      const application = await getApplication(applicationId);

      dispatch({ type: "FETCH_SUCCESS", payload: { data: { application, dictionary: phrases } } });
    } catch {
      dispatch({ type: "FETCH_ERROR" });
    }
  };

  const renderContent = () => {
    if (data.application) {
      if (data.application.type === "DUP") {
        return (
          <DupQuotationOverview
            dictionary={data.dictionary}
            defaultCoverage={data.application.data.defaultCoverage}
            paymentTerm={data.application.data.paymentTerm}
            insuredPersons={data.application.data.insuredPersons}
            premium={data.application.data.premium ?? null}
            policyCosts={data.application.data.oneTimePolicyCosts ?? "Onbekend"}
          />
        );
      }
      return (
        <DlpQuotationOverview
          dictionary={data.dictionary}
          premium={data.application.data.premium ?? null}
          coverages={data.application.data.coverages}
          crosswiseInsured={data.application.data.crosswiseInsured === "Yes"}
          insuranceTaker={data.application.data.insuranceTaker ?? []}
          insurers={data.application.data.insuredPersons}
          paymentTerm={data.application.data.paymentTerm}
          mortgageSupplier={data.application.data.mortgageSupplierLabel}
        />
      );
    }

    const message = data.loading ? fields.LoadingMessage.value : fields.ErrorMessage.value;

    return <SearchLoader text={message} />;
  };

  return (
    <div className={styles.applicationDetails__wrapper}>
      <div className={styles.applicationDetails__header}>
        <div className={styles.applicationDetails__button}>
          <BackButton>
            <Text field={fields.BackButton} />
          </BackButton>
        </div>
        <Button
          leading={<i className={`icon icon-download ${styles.applicationDetails__printIcon}`} />}
          disabled={data.loading || !data.application}
          onClick={() => window.print()}
        >
          PDF Download
        </Button>
      </div>
      {renderContent()}
    </div>
  );
};
